/**
 * PolicyCard Component
 * Displays policy summary with status badge, TIV, address, and fire zone indicator
 */

import { forwardRef } from 'react';
import { Flame } from 'lucide-react';
import type { Policy, PolicyStatus } from '../types/api';
import { cn } from '../utils/cn';
import { formatAddress } from '../utils/formatAddress';
import { Badge } from './Badge';
import { Button } from './Button';
import { Card } from './Card';

// ============================================================================
// Types
// ============================================================================

export interface PolicyCardProps {
  policy: Policy;
  onClick?: () => void;
  showActions?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  className?: string;
}

// ============================================================================
// Status Configuration
// ============================================================================

const statusConfig: Record<PolicyStatus, {
  variant: 'danger' | 'warning' | 'success' | 'info' | 'default';
  label: string;
}> = {
  action_req: { variant: 'danger', label: 'Action Required' },
  waiting: { variant: 'warning', label: 'Waiting' },
  approved: { variant: 'success', label: 'Approved' },
  reviewing: { variant: 'info', label: 'Reviewing' },
};

// ============================================================================
// Helpers
// ============================================================================

/**
 * Format currency value
 */
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

// ============================================================================
// Component
// ============================================================================

export const PolicyCard = forwardRef<HTMLDivElement, PolicyCardProps>(
  ({ policy, onClick, showActions = false, onEdit, onDelete, className }, ref) => {
    const statusInfo = statusConfig[policy.status];

    return (
      <Card
        ref={ref}
        hoverable={Boolean(onClick)}
        onClick={onClick}
        className={cn('relative', className)}
      >
        {/* Fire Zone Indicator */}
        {policy.inFireZone && (
          <div className="absolute top-4 right-4" title="In Fire Zone">
            <Flame size={14} className="text-gray-500 dark:text-slate-400" aria-label="Fire zone" />
          </div>
        )}

        {/* Policy Header */}
        <div className="flex items-start justify-between pr-10">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
              {policy.insuredName}
            </h3>
            <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
              Policy #{policy.policyNumber}
            </p>
          </div>
          <Badge variant={statusInfo.variant} size="sm">
            {statusInfo.label}
          </Badge>
        </div>

        {/* TIV */}
        <div className="mt-4">
          <p className="text-sm text-gray-500 dark:text-slate-400">Total Insured Value</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-slate-100">
            {formatCurrency(policy.tiv)}
          </p>
        </div>

        {/* Address */}
        <div className="mt-4">
          <p className="text-sm text-gray-500 dark:text-slate-400">Property Address</p>
          <p className="text-sm text-gray-900 dark:text-slate-100 mt-1">{formatAddress(policy)}</p>
        </div>

        {/* Fire Zone Distance */}
        {policy.inFireZone && policy.fireZoneDistance !== undefined && (
          <div className="mt-3 flex items-center text-sm text-gray-500 dark:text-slate-400">
            <svg
              className="h-4 w-4 mr-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            {policy.fireZoneDistance.toFixed(1)} miles from fire perimeter
          </div>
        )}

        {/* Actions */}
        {showActions && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-slate-700 flex gap-2">
            {onEdit && (
              <Button
                variant="secondary"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
              >
                Edit
              </Button>
            )}
            {onDelete && (
              <Button
                variant="danger"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
              >
                Delete
              </Button>
            )}
          </div>
        )}
      </Card>
    );
  }
);

PolicyCard.displayName = 'PolicyCard';

export default PolicyCard;
